import type { SetStamp, StripGeometry } from '@renderer/lib/stickers/layoutSet'

/**
 * A "scatter" set: many small pieces thrown over every photograph, like foil
 * stars on a photobooth strip, instead of one cluster stamped at three
 * anchors.
 *
 * The set is ONE sticker in the Studio — so it is one tile in the tray and one
 * entry in `stickerIds` — whose image is a sprite of square pieces side by side
 * (five stars: 2560x512). The booth cuts the sprite into its pieces and lays
 * several on each photo. The Studio needs nothing new: a sticker is a scatter
 * set when its name says "scatter".
 */
export const isScatterSet = (sticker: { name?: string }): boolean =>
  /\bscatter\b/i.test(sticker.name ?? '')

/** One piece on the strip: where it goes, and which cell of the sprite it is. */
export interface ScatterStamp extends SetStamp {
  piece: number
  /** Index of the photo window it was thrown at, in `drawParams.images` photo order. */
  window: number
}

/** Pieces per photograph: four or five, so the photos do not all look stamped. */
const MIN_PER_PHOTO = 4
const MAX_PER_PHOTO = 5
/** A piece's width as a share of its photo's width. */
const MIN_SIZE = 0.13
const MAX_SIZE = 0.21
const MAX_TILT_DEG = 28
/**
 * Where the face usually is, in photo units: pieces keep out of this ellipse
 * so a star never lands on an eye.
 */
const FACE = { cx: 0.5, cy: 0.42, rx: 0.3, ry: 0.32 }
/** How far a piece may sit past the photo's edge, onto the border, in photo units. */
const OVERHANG = 0.06
/** Closest two pieces on one photo may be, centre to centre, in photo widths. */
const MIN_GAP = 0.2

/**
 * Where every piece goes: several per photo window, scattered round the edges
 * and corners, clear of the face, at varied sizes and tilts.
 *
 * Deterministic in `seed`, so the preview the guest approves is the print.
 */
export function layoutScatter(
  geometry: StripGeometry | undefined,
  pieceCount: number,
  seed: string
): ScatterStamp[] {
  if (!geometry || pieceCount < 1) return []
  const W = geometry.canvasWidth
  const H = geometry.canvasHeight
  const random = rng(seed)
  const between = (lo: number, hi: number): number => lo + random() * (hi - lo)

  const windows = geometry.images.filter((image) => image.type.startsWith('photo-'))
  const stamps: ScatterStamp[] = []
  let lastPiece = -1

  windows.forEach((window, windowIndex) => {
    const want = MIN_PER_PHOTO + Math.floor(random() * (MAX_PER_PHOTO - MIN_PER_PHOTO + 1))
    const placed: { u: number; v: number }[] = []

    for (let attempt = 0; placed.length < want && attempt < 200; attempt++) {
      const u = between(-OVERHANG, 1 + OVERHANG)
      const v = between(-OVERHANG, 1 + OVERHANG)
      const inFace = ((u - FACE.cx) / FACE.rx) ** 2 + ((v - FACE.cy) / FACE.ry) ** 2 < 1
      if (inFace) continue
      const aspect = window.height / window.width
      const tooClose = placed.some((p) => Math.hypot(p.u - u, (p.v - v) * aspect) < MIN_GAP)
      if (tooClose) continue
      placed.push({ u, v })

      // Never the same piece twice running, so neighbours differ in colour.
      let piece = Math.floor(random() * pieceCount)
      if (pieceCount > 1 && piece === lastPiece) piece = (piece + 1) % pieceCount
      lastPiece = piece

      const width = between(MIN_SIZE, MAX_SIZE) * window.width
      const x = Math.min(W, Math.max(0, window.left + u * window.width))
      const y = Math.min(H, Math.max(0, window.top + v * window.height))
      stamps.push({
        xPct: (x / W) * 100,
        yPct: (y / H) * 100,
        widthPct: (width / W) * 100,
        rotation: between(-MAX_TILT_DEG, MAX_TILT_DEG),
        piece,
        window: windowIndex
      })
    }
  })
  return stamps
}

/** Small deterministic PRNG (FNV-1a seed, mulberry32 stream). */
function rng(seed: string): () => number {
  let h = 2166136261
  for (let i = 0; i < seed.length; i++) h = Math.imul(h ^ seed.charCodeAt(i), 16777619)
  let a = h >>> 0
  return () => {
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Sprite URL → its pieces as PNG data URLs. One cut per sprite per booth run. */
const pieceCache = new Map<string, Promise<string[]>>()

/**
 * Cut a horizontal sprite of square cells into one image per cell.
 *
 * The cell count is the sprite's width over its height, so a 2560x512 sprite
 * is five pieces. Data URLs rather than object URLs: the print bake loads each
 * placement's `url` again, possibly after this page has gone.
 */
export function spritePieces(url: string): Promise<string[]> {
  const cached = pieceCache.get(url)
  if (cached) return cached
  const cut = new Promise<string[]>((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = (): void => {
      const size = img.naturalHeight
      const count = Math.max(1, Math.round(img.naturalWidth / size))
      const pieces: string[] = []
      for (let i = 0; i < count; i++) {
        const canvas = document.createElement('canvas')
        canvas.width = size
        canvas.height = size
        const ctx = canvas.getContext('2d')
        if (!ctx) return reject(new Error('no 2d context'))
        ctx.drawImage(img, i * size, 0, size, size, 0, 0, size, size)
        pieces.push(canvas.toDataURL('image/png'))
      }
      resolve(pieces)
    }
    img.onerror = (): void => reject(new Error(`sprite failed to load: ${url}`))
    img.src = url
  })
  pieceCache.set(url, cut)
  cut.catch(() => pieceCache.delete(url))
  return cut
}
